import { JsonObject, JsonProperty } from "typescript-json-serializer";

import {WidgetConfiguration} from "./widget-configuration";
import {MapViewConfiguration} from "./view-configuration";
import {PortalConfiguration} from "./portal-configuration";
import {BasemapConfiguration} from "./map-configuration";

@JsonObject()
export class LocalBasemapsSourceConfiguration {
    /**
     * A collection of [Basemap](https://developers.arcgis.com/javascript/latest/api-reference/esri-Basemap.html)s.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery-support-LocalBasemapsSource.html#basemaps)
     */
    // @JsonProperty()
    // basemaps?: CollectionProperties<BasemapProperties>;

    constructor() {}
}

@JsonObject()
export class PortalBasemapsSourceConfiguration extends LocalBasemapsSourceConfiguration {
    /**
     * Function used to filter basemaps after being fetched from the Portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery-support-PortalBasemapsSource.html#filterFunction)
     */
    // filterFunction: BasemapFilter;
    /**
     * The Portal from which to fetch basemaps.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery-support-PortalBasemapsSource.html#portal)
     */
    @JsonProperty()
    declare portal?: PortalConfiguration;
    /**
     * An object with key-value pairs used to create a custom basemap gallery group query.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery-support-PortalBasemapsSource.html#query)
     */
    @JsonProperty()
    declare query?: any | string;

    /**
     * The source's state.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery-support-PortalBasemapsSource.html#state)
     *
     * @default not-loaded
     */
    @JsonProperty()
    declare state: "not-loaded" | "loading" | "ready";

    constructor() {
        super();
    }
}
const localOrPortalConfiguration = (source: LocalBasemapsSourceConfiguration | PortalBasemapsSourceConfiguration) => {
    return source && (source as any).query !== undefined
        ? PortalBasemapsSourceConfiguration: PortalBasemapsSourceConfiguration;
};

/**
 * *Geo.js Properties*
 */
@JsonObject()
export class BasemapStyleConfiguration {

    @JsonProperty()
    declare itemId: string;

    @JsonProperty()
    declare url?: string;

    constructor() {}
}

@JsonObject()
export class BasemapGalleryConfiguration extends WidgetConfiguration {
    /**
     * The map's [basemap](https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#basemap).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery.html#activeBasemap)
     */
    @JsonProperty()
    declare activeBasemap?: BasemapConfiguration;

    /**
     * When `true`, sets the widget to a disabled state so the user cannot interact with it.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery.html#disabled)
     */
    @JsonProperty()
    declare disabled: boolean;

    /**
     * Indicates the heading level to use for the message "No basemaps available" when no basemaps are available in the BasemapGallery.
     *
     * @default 2
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery.html#headingLevel)
     */
    @JsonProperty()
    declare headingLevel?: number;
    /**
     * The source for basemaps that the widget will display.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery.html#source)
     */
    @JsonProperty({type: localOrPortalConfiguration})
    declare source?: LocalBasemapsSourceConfiguration | PortalBasemapsSourceConfiguration;
    /**
     * The view from which the widget will operate. This view provides access to the active [basemap](https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#basemap) via the view's [map](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#map) property.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery.html#view)
     */
    @JsonProperty()
    declare view?: (MapViewConfiguration & { type: "2d" }); // | (SceneViewConfiguration & { type: "3d" });

    /**
     * *Geo.js Properties*
     * Enable or disable search field in BasemapGalleryWidget
     */
    @JsonProperty()
    declare searchEnabled?: boolean;

    /**
     * *Geo.js Properties*
     * Defines the set of Basemaps available in the application as an Array of Basemap titles. If defined, exludeApplicationBasemaps and excludeTags are ignored.
     * Basemaps can be selected from the global Basemaps Group: https://www.geo.lu.ch/geoshare/home/group.html?id=d621f41867994b0aa1207612ad5fcc76
     */
    @JsonProperty()
    declare applicationBasemaps?: string[] | "none";

    /**
     * *Geo.js Properties*
     * Defines the set of Basemaps excluded in the application as an Array of Basemap titles.
     * Basemaps can be selected from the global Basemaps Group: https://www.geo.lu.ch/geoshare/home/group.html?id=d621f41867994b0aa1207612ad5fcc76
     */
    @JsonProperty()
    declare excludeApplicationBasemaps?: string[];

    /**
     * *Geo.js Properties*
     * Defines the default Basemap title. If defined, Basemap of Geoshare Webmap is ignored
     * Basemaps can be selected from the global Basemaps Group: https://www.geo.lu.ch/geoshare/home/group.html?id=d621f41867994b0aa1207612ad5fcc76
     */
    @JsonProperty()
    declare default?: string | null;

    /**
     * *Geo.js Properties*
     * Defines a list of tags. Basemaps with one of these tags are ignored.
     * Basemaps can be selected from the global Basemaps Group: https://www.geo.lu.ch/geoshare/home/group.html?id=d621f41867994b0aa1207612ad5fcc76
     */
    @JsonProperty()
    declare excludeTags?: string[];

    /**
     * *Geo.js Properties*
     * The opacity of the basemap-layer. This value can range between 1 and 0, where 0 is 100 percent transparent
     * and 1 is completely opaque. If defined, Basemap opacity in the Geoshare Webmap is ignored
     * https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-Layer.html#opacity
     */
    @JsonProperty()
    declare opacity?: number | null;

    /**
     * *Geo.js Properties*
     * Custom Basmap Styles
     */
    @JsonProperty()
    declare styles?: Map<string, BasemapStyleConfiguration[]>;

    constructor() {
        super();
    }
}
